import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Announcement,
  Button,
  Card,
  getFormattedLongDate,
  Layout,
  LoaderSkeleton,
  ModalV2 as Modal,
  parseLocalDate,
  Pill,
  Search,
  Select,
  SubscriptionIcon,
  TableData,
  Tabs,
  toast,
} from '@utilities';

import { readClientUsersSubscription } from '@api/client';
import { addUserToSubscription, removeUserFromSubscription } from '@api/user';
import { userFetch } from '@redux/slices/user';

import SubscriptionOverview from '../../../../components/SubscriptionOverview';

import { createUserSubscriptionColumns } from './utilities/helpers';

import styles from './_index.module.scss';

const VerifiedVoicesSubscription = () => {
  const dispatch = useDispatch();
  const [addingUsers, setAddingUsers] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [removingUsers, setRemovingUsers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [users, setUsers] = useState([]);

  const { clientInfo, id, permissions } = useSelector((state) => state.user?.data || {});
  const isEditable = permissions?.canEditSurveySubscription;
  const userSubscriptionId = clientInfo?.subscriptions?.[0]?.subscriptionId;
  const subscriptionDetails = clientInfo?.subscriptionDetails || [];

  const userSubscriptionIdSet = useMemo(() => {
    // Currently 1:1, but using a Set to support users with multiple subscriptions in the future
    return new Set(userSubscriptionId ? [userSubscriptionId] : []);
  }, [userSubscriptionId]);

  useEffect(() => {
    setSubscriptions(subscriptionDetails);
  }, [subscriptionDetails]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      let allUsers = [];
      let currentPage = 1;
      const pageSize = 100;
      const maxPages = 200;

      while (currentPage <= maxPages) {
        const userSubscription = await readClientUsersSubscription({
          page: currentPage,
          pageSize,
        });

        if (!userSubscription || typeof userSubscription !== 'object') {
          break;
        }

        const results = userSubscription.results || [];
        if (results.length > 0) {
          allUsers.push(...results);
        }

        const hasNext = userSubscription?.next != null;

        if (!hasNext || results.length === 0) {
          break;
        }

        currentPage++;
      }

      return allUsers;
    };

    const fetchSubscriptionUsers = async () => {
      setError(null);
      setIsLoading(true);

      try {
        const allUsers = await fetchAllUsers();
        setUsers(allUsers);
      } catch (err) {
        setError(`Failed to load subscription info: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionUsers();
  }, []);

  const tableData = useMemo(
    () =>
      (users || []).map((user) => ({
        current: Number(user.periodCompletesUsed ?? 0),
        id: user.id,
        isSubscribed: !!user.isSubscribed,
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || '—',
        previous: Number(user.previousPeriodCompletesUsed ?? 0),
        subscriptionId: user.subscriptionId,
        subscriptionName: user.subscriptionName,
        type: user.type || '',
      })),
    [users]
  );

  const filteredData = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();
    if (!query) return tableData;

    const tokens = query.split(/\s+/);
    return tableData.filter((row) => {
      const haystack = `${row.name} ${row.type}`.toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [searchQuery, tableData]);

  const visibleSubscriptions = useMemo(() => {
    const userSubscriptions = subscriptions.filter(({ id }) => userSubscriptionIdSet.has(id));
    return isEditable
      ? [...userSubscriptions, ...subscriptions.filter(({ id }) => !userSubscriptionIdSet.has(id))]
      : userSubscriptions;
  }, [isEditable, subscriptions, userSubscriptionIdSet]);

  const pendingUser = users.find((user) => user.id === pendingUserId);
  const pendingUserName = [pendingUser?.firstName, pendingUser?.lastName].filter(Boolean).join(' ');

  const handleAddToSubscription = async ({ subscriptionId, userId }) => {
    setAddingUsers((prev) => ({ ...prev, [userId]: true }));
    try {
      await addUserToSubscription({ subscriptionId, userId });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                isSubscribed: true,
                subscriptionId,
                subscriptionName:
                  subscriptions.find((subscription) => subscription.id === subscriptionId)?.name ||
                  '',
              }
            : user
        )
      );

      setSubscriptions((prev) =>
        prev.map((subscription) =>
          subscription.id === subscriptionId
            ? { ...subscription, surveyCreatorsCount: (subscription.surveyCreatorsCount ?? 0) + 1 }
            : subscription
        )
      );

      if (userId === id) {
        dispatch(userFetch());
      }

      toast.success('User added to subscription successfully.');
    } catch (err) {
      toast.error(`Failed to add user: ${err.message}`);
    } finally {
      setAddingUsers((prev) => ({ ...prev, [userId]: false }));
      setPendingUserId(null);
    }
  };

  const canUpdateSubscription = users.some((user) => user?.id === id && user?.type === 'admin');
  const handleRemoveFromSubscription = async ({ subscriptionId, userId }) => {
    setRemovingUsers((prev) => ({ ...prev, [userId]: true }));
    try {
      await removeUserFromSubscription({ subscriptionId, userId });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                isSubscribed: false,
                subscriptionId: null,
                subscriptionName: '',
              }
            : user
        )
      );

      setSubscriptions((prev) =>
        prev.map((subscription) =>
          subscription.id === subscriptionId
            ? { ...subscription, surveyCreatorsCount: (subscription.surveyCreatorsCount ?? 0) - 1 }
            : subscription
        )
      );

      if (userId === id) {
        dispatch(userFetch());
      }

      toast.success('User removed from subscription successfully.');
    } catch (err) {
      toast.error('Failed to remove user from subscription');
    } finally {
      setRemovingUsers((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const columns = createUserSubscriptionColumns({
    addingMap: addingUsers,
    canUpdateSubscription,
    onAddToSubscription: (userId) => {
      setPendingUserId(userId);
      setSelectedSubscriptionId(null);
    },
    onRemoveFromSubscription: handleRemoveFromSubscription,
    removingMap: removingUsers,
    tableData,
  });

  if (isLoading) {
    return (
      <LoaderSkeleton height={115}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );
  }

  if (error || !visibleSubscriptions.length || !users) {
    return (
      <div>
        <Announcement
          header="No subscription information available."
          text="Please contact your consultant for more information."
          variant="info"
        />
      </div>
    );
  }

  return (
    <div className={styles['vv-subscription']}>
      <div className={styles['vv-subscription-overview']}>
        {isEditable && <Pill isDisabled value="Admin" />}
      </div>
      <Tabs
        aria-label="Verified Voices subscription view"
        onChange={setSelectedTab}
        options={[
          { label: 'Overview', value: 'overview' },
          { label: 'Users', value: 'users' },
        ]}
        value={selectedTab}
      />
      {selectedTab === 'overview' && (
        <div className={styles['vv-subscription-overview']}>
          {visibleSubscriptions.length > 1 && <h2>Subscriptions</h2>}
          {visibleSubscriptions.map((subscription) => (
            <Card
              as={Layout.Flex.Column}
              gap="small"
              key={subscription.id}
              padding="large"
              statusColor={
                userSubscriptionIdSet.has(subscription.id)
                  ? 'var(--color-background-success-bold)'
                  : undefined
              }
            >
              <h3>{subscription?.name}</h3>
              <Layout.Flex.Row
                className={styles['vv-subscription-overview-content']}
                gap="xx-large"
              >
                <Layout.Flex.Column className={styles['vv-subscription-overview-section']}>
                  <SubscriptionOverview subscription={subscription} />
                </Layout.Flex.Column>
                <div className={styles['vv-subscription-divider']} />
                <Layout.Flex.Column
                  className={styles['vv-subscription-overview-section']}
                  gap="small"
                >
                  <p>
                    Subscription Start Date:{' '}
                    <strong>
                      {subscription?.startDate
                        ? getFormattedLongDate(parseLocalDate(subscription.startDate))
                        : 'N/A'}
                    </strong>
                  </p>
                  <p>
                    Subscription End Date:{' '}
                    <strong>
                      {subscription?.endDate
                        ? getFormattedLongDate(parseLocalDate(subscription.endDate))
                        : 'N/A'}
                    </strong>
                  </p>
                  <p>
                    Subscription Completes:{' '}
                    <strong>{subscription?.hardCap?.toLocaleString()}</strong>
                  </p>
                  <p className={styles['vv-subscription-icon-row']}>
                    <SubscriptionIcon />
                    <span>
                      Survey Creators: <strong>{subscription.surveyCreatorsCount}</strong>
                    </span>
                  </p>
                </Layout.Flex.Column>
              </Layout.Flex.Row>
            </Card>
          ))}
        </div>
      )}
      {selectedTab === 'users' && (
        <div className={styles['vv-subscription-users']}>
          <h2>Users</h2>
          <Search
            className={styles['vv-subscription-search']}
            isSearching={isLoading && searchQuery.trim().length > 0}
            onChange={(query) => setSearchQuery(query)}
            placeholder="Search for a user"
            value={searchQuery}
          />
          {filteredData.length === 0 && searchQuery.trim().length > 0 ? (
            <div className={styles['vv-subscription-empty-message']}>
              <h3>No results found.</h3>
              <p>Try refining your search.</p>
            </div>
          ) : (
            <TableData columns={columns} data={filteredData} isLoading={isLoading} />
          )}
        </div>
      )}
      {pendingUserId && (
        <Modal
          buttons={[
            <Button
              key="cancel"
              onClick={() => setPendingUserId(null)}
              text="Cancel"
              variant="secondary"
            />,
            <Button
              isDisabled={!selectedSubscriptionId}
              isLoading={addingUsers[pendingUserId]}
              key="confirm"
              onClick={() => {
                handleAddToSubscription({
                  subscriptionId: selectedSubscriptionId,
                  userId: pendingUserId,
                });
              }}
              text="Save"
            />,
          ]}
          isActive={!!pendingUserId}
          onClose={() => setPendingUserId(null)}
          title="Manage User"
        >
          <p className={styles['vv-subscription-modal-text']}>
            Add <strong>{pendingUserName}</strong> to the following subscription:
          </p>
          <Select
            className={styles['vv-subscription-modal-select']}
            label="SUBSCRIPTION"
            onChange={(option) => setSelectedSubscriptionId(option.value)}
            options={subscriptions.map((subscription) => ({
              label: subscription.name,
              value: subscription.id,
            }))}
            placeholder="Choose a subscription"
            value={
              subscriptions
                .map((subscription) => ({ label: subscription.name, value: subscription.id }))
                .find((option) => option.value === selectedSubscriptionId) || null
            }
          />
        </Modal>
      )}
    </div>
  );
};

export default VerifiedVoicesSubscription;
